import { mm } from "@inovitas/infra3dsdk";
import { Box, Skeleton, Typography } from "@mui/material";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import ClientConfig from "shared/config";

import UseAuth from "shared/common/hooks/UseAuth";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import {
  getApiKeyHeaders,
  getAuthorizationHeaders,
} from "shared/common/utils/general-utils";

// Constants
const PREVIEW_WIDTH = 300;
const PREVIEW_HEIGHT = 200;
const PREVIEW_MARGIN = 8;
const PREVIEW_OFFSET = 32;

const filegateUrl = new URL(ClientConfig.filegateURL);
const filegateFilesUrl = new URL("files/", filegateUrl);
const imageFileExtensionRegex =
  /\.(avif|bmp|gif|heic|heif|ico|jpe?g|png|svg|tiff?|webp)$/i;

// Blob cache
const MAX_BLOB_CACHE_SIZE = 20;
const blobUrlCache = new Map<string, string>();

const addToBlobCache = (url: string, objectUrl: string): void => {
  if (blobUrlCache.size >= MAX_BLOB_CACHE_SIZE) {
    const firstKey = blobUrlCache.keys().next().value!;
    URL.revokeObjectURL(blobUrlCache.get(firstKey)!);
    blobUrlCache.delete(firstKey);
  }
  blobUrlCache.set(url, objectUrl);
};

// URL utilities
const isProtectedFilegateUrl = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url);
    return (
      parsedUrl.origin === filegateFilesUrl.origin &&
      parsedUrl.pathname.startsWith(filegateFilesUrl.pathname)
    );
  } catch {
    return false;
  }
};

const isHttpUrl = (candidate: string): boolean => {
  try {
    const parsed = new URL(candidate);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const rewriteInfra3dFileUrl = (candidate: string): string => {
  try {
    const parsed = new URL(candidate);
    if (parsed.pathname.startsWith("/files/")) {
      const relativeFilePath = `${parsed.pathname.slice(1)}${parsed.search}${parsed.hash}`;
      return new URL(relativeFilePath, filegateUrl).toString();
    }
  } catch {
    // Keep original candidate if URL parsing fails.
  }

  return candidate;
};

const isImageUrl = (candidate: string): boolean => {
  try {
    if (isProtectedFilegateUrl(candidate)) return true;
    const parsed = new URL(candidate);
    return imageFileExtensionRegex.test(parsed.pathname);
  } catch {
    return false;
  }
};

const findFirstUrl = (value: unknown): string | null => {
  const candidates = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? value.split(/[;,]/)
      : [];

  for (const item of candidates) {
    if (typeof item !== "string") continue;
    const entry = item.trim();
    if (!entry || !isHttpUrl(entry)) continue;
    const rewritten = rewriteInfra3dFileUrl(entry);
    if (isImageUrl(rewritten)) return rewritten;
  }

  return null;
};

export const relevantImageUrl = (feature: mm.Feature): string | null => {
  const { properties } = feature.toGeoJSON();
  if (!properties) return null;

  for (const value of Object.values(properties)) {
    const found = findFirstUrl(value);
    if (found) return found;
  }

  return null;
};

// Position helper
interface HoverPosition {
  top: number;
  left: number;
}

const getHoveredPreviewPosition = (pointer: HoverPosition): HoverPosition => {
  const left = Math.max(
    PREVIEW_WIDTH / 2 + PREVIEW_MARGIN * 2,
    Math.min(
      window.innerWidth - PREVIEW_WIDTH / 2 - PREVIEW_MARGIN * 2,
      pointer.left
    )
  );
  const spaceAbove = pointer.top - PREVIEW_OFFSET;
  const top =
    spaceAbove >= PREVIEW_HEIGHT + PREVIEW_MARGIN
      ? pointer.top - PREVIEW_HEIGHT - PREVIEW_OFFSET
      : pointer.top + PREVIEW_OFFSET;
  return { top, left };
};

// Popover presentational component
interface HoveredImagePopoverProps {
  isVisible: boolean;
  imageLoading: boolean;
  imageSrc: string | null;
  onImageLoad: () => void;
  onImageError: () => void;
  loadErrorText: string;
  position: HoverPosition | null;
}

function HoveredImagePopover({
  isVisible,
  imageLoading,
  imageSrc,
  onImageLoad,
  onImageError,
  loadErrorText,
  position,
}: HoveredImagePopoverProps) {
  if (!isVisible || !position) {
    return null;
  }

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <Box
      id="hovered-image-popover"
      sx={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
        transform: "translateX(-50%)",
        zIndex: 80,
        backgroundColor: "white",
        borderRadius: "8px",
        padding: 1,
        pointerEvents: "none",
      }}
    >
      {imageSrc ? (
        <Box
          sx={{
            position: "relative",
            width: `${PREVIEW_WIDTH}px`,
            height: `${PREVIEW_HEIGHT}px`,
            display: "flex",
            justifyContent: "center",
          }}
        >
          {imageLoading && (
            <Skeleton
              variant="rounded"
              width={PREVIEW_WIDTH}
              height={PREVIEW_HEIGHT}
              sx={{
                borderRadius: "4px",
                position: "absolute",
                inset: 0,
              }}
            />
          )}
          <img
            src={imageSrc}
            alt="Hovered feature"
            onLoad={onImageLoad}
            onError={onImageError}
            style={{
              display: "block",
              height: `${PREVIEW_HEIGHT}px`,
              objectFit: "cover",
              borderRadius: "4px",
              visibility: imageLoading ? "hidden" : "visible",
            }}
          />
        </Box>
      ) : imageLoading ? (
        <Skeleton
          variant="rounded"
          width={PREVIEW_WIDTH}
          height={PREVIEW_HEIGHT}
          sx={{ borderRadius: "4px" }}
        />
      ) : (
        <Box
          sx={{
            width: `${PREVIEW_WIDTH}px`,
            height: `${PREVIEW_HEIGHT}px`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary" align="center">
            {loadErrorText}
          </Typography>
        </Box>
      )}
    </Box>,
    document.body
  );
}

// Public handle
export interface HoverImagePreviewHandle {
  onFeatureHover: (url: string | null) => void;
}

// Main component
const HoverImagePreview = forwardRef<HoverImagePreviewHandle>(
  function HoverImagePreview(_, ref) {
    const { t } = useTranslation();
    const { tokens } = UseAuth();

    const viewer = defaultStore((state) => state.viewer);
    const infosPopover = sidebarStore((state) => state.infosPopover);

    const [hoveredImageUrl, setHoveredImageUrl] = useState<string | null>(null);
    const [hoveredImageSrc, setHoveredImageSrc] = useState<string | null>(null);
    const [hoveredImageLoading, setHoveredImageLoading] = useState(false);
    const [hoveredImageLoadError, setHoveredImageLoadError] = useState(false);
    const [hoveredPosition, setHoveredPosition] =
      useState<HoverPosition | null>(null);
    const pointerRef = useRef<HoverPosition | null>(null);

    useImperativeHandle(ref, () => ({
      onFeatureHover(url: string | null) {
        setHoveredImageUrl((currentUrl) =>
          currentUrl === url ? currentUrl : url
        );

        if (url && pointerRef.current) {
          const { top, left } = getHoveredPreviewPosition(pointerRef.current);
          setHoveredPosition((currentPosition) =>
            currentPosition?.top === top && currentPosition?.left === left
              ? currentPosition
              : { top, left }
          );
        } else {
          setHoveredPosition((currentPosition) =>
            currentPosition === null ? currentPosition : null
          );
        }
      },
    }));

    useEffect(() => {
      if (!viewer || !infosPopover) {
        setHoveredPosition((currentPosition) =>
          currentPosition === null ? currentPosition : null
        );
        setHoveredImageUrl((currentUrl) =>
          currentUrl === null ? currentUrl : null
        );
        pointerRef.current = null;
        return;
      }

      const canvas = viewer.getCanvas();
      const handlePointerMove = (event: PointerEvent) => {
        pointerRef.current = { top: event.clientY, left: event.clientX };
        const { top, left } = getHoveredPreviewPosition(pointerRef.current);
        setHoveredPosition((currentPosition) =>
          currentPosition?.top === top && currentPosition?.left === left
            ? currentPosition
            : { top, left }
        );
      };

      canvas.addEventListener("pointermove", handlePointerMove);
      return () => canvas.removeEventListener("pointermove", handlePointerMove);
    }, [viewer, infosPopover]);

    useEffect(() => {
      if (!hoveredImageUrl || !infosPopover) {
        setHoveredImageSrc(null);
        setHoveredImageLoading(false);
        setHoveredImageLoadError(false);
        return;
      }

      const controller = new AbortController();
      const isProtectedImage = isProtectedFilegateUrl(hoveredImageUrl);
      setHoveredImageSrc(null);
      setHoveredImageLoading(true);
      setHoveredImageLoadError(false);

      const loadImageWithAuth = async () => {
        if (!isProtectedImage) {
          setHoveredImageSrc(hoveredImageUrl);
          return;
        }

        const cachedObjectUrl = blobUrlCache.get(hoveredImageUrl);
        if (cachedObjectUrl) {
          setHoveredImageSrc(cachedObjectUrl);
          return;
        }

        if (!tokens?.access_token) {
          setHoveredImageSrc(null);
          setHoveredImageLoading(false);
          setHoveredImageLoadError(true);
          return;
        }

        try {
          const response = await fetch(hoveredImageUrl, {
            signal: controller.signal,
            headers: {
              ...getAuthorizationHeaders(tokens.access_token),
              ...getApiKeyHeaders(),
            },
          });

          if (!response.ok)
            throw new Error(`Image fetch failed: ${response.status}`);
          const contentType = response.headers.get("content-type");
          if (!contentType || !contentType.startsWith("image/")) {
            throw new Error(
              `Protected hover URL does not point to an image: ${contentType ?? "unknown content-type"}`
            );
          }

          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);
          addToBlobCache(hoveredImageUrl, objectUrl);
          setHoveredImageSrc(objectUrl);
        } catch (error) {
          if ((error as Error).name === "AbortError") return;
          console.error("Failed to load protected hover image", error);
          setHoveredImageSrc(null);
          setHoveredImageLoading(false);
          setHoveredImageLoadError(true);
        }
      };

      loadImageWithAuth();

      return () => {
        controller.abort();
        // Object URL is kept in cache for reuse; no revocation here
      };
    }, [hoveredImageUrl, infosPopover, tokens?.access_token]);

    const showHoveredImagePopover =
      infosPopover &&
      Boolean(hoveredPosition) &&
      Boolean(hoveredImageUrl) &&
      (hoveredImageLoading ||
        Boolean(hoveredImageSrc) ||
        hoveredImageLoadError);

    return (
      <HoveredImagePopover
        isVisible={showHoveredImagePopover}
        imageLoading={hoveredImageLoading}
        imageSrc={hoveredImageSrc}
        onImageLoad={() => setHoveredImageLoading(false)}
        onImageError={() => {
          setHoveredImageSrc(null);
          setHoveredImageLoading(false);
          setHoveredImageLoadError(true);
        }}
        loadErrorText={t("tooltip.image_load_failed")}
        position={hoveredPosition}
      />
    );
  }
);

export default HoverImagePreview;
